import { ICON_LLMARK, ICON_GEAR, ICON_DOTS, ICON_TRANSLATE } from './icons.js';
import type { TranslateFn } from './i18n.js';

const STORAGE_KEY = 'llais.pill.active';

export type PillCallbacks = {
  onToggle: (active: boolean) => void;
  onSettings: () => void;
  initialTranslator: TranslateFn;
};

export type PillState = 'idle' | 'loading' | 'playing';

export type PillStateMeta = {
  label?: string;
  // When loading, indicates whether the operation is a translation
  // hop (true) or plain synthesis (false). Drives the icon choice:
  // translate-glyph vs the generic three-dot "working" indicator.
  translating?: boolean;
};

export class Pill {
  private el: HTMLDivElement;
  private toggleBtn: HTMLButtonElement;
  private gearBtn: HTMLButtonElement;
  private statusEl: HTMLSpanElement;
  private active = false;
  private playState: PillState = 'idle';
  private stateLabel = '';
  private translatingFlag = false;
  private tr: TranslateFn;

  constructor(private cb: PillCallbacks) {
    this.tr = cb.initialTranslator;
    this.el = document.createElement('div');
    this.el.className = 'pill';
    this.el.setAttribute('role', 'group');
    this.el.setAttribute('aria-label', 'Llais — Welsh text-to-speech');
    this.el.innerHTML = `
      <button class="pill-toggle" aria-pressed="false" aria-label="Toggle Llais on/off">
        <span class="pill-icon">${ICON_LLMARK}</span>
        <span class="pill-status" aria-live="polite">Llais</span>
      </button>
      <button class="pill-gear" aria-label="Settings" tabindex="-1">${ICON_GEAR}</button>
    `;

    this.toggleBtn = this.el.querySelector('.pill-toggle')!;
    this.gearBtn = this.el.querySelector('.pill-gear')!;
    this.statusEl = this.el.querySelector('.pill-status')!;

    this.toggleBtn.addEventListener('click', () => this.toggle());
    this.gearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.active) return;
      this.cb.onSettings();
    });

    try {
      this.active = localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      /* ignore */
    }
    this.render();
  }

  get root(): HTMLElement {
    return this.el;
  }

  get isActive(): boolean {
    return this.active;
  }

  applyInitial(): void {
    if (this.active) this.cb.onToggle(true);
  }

  setState(state: PillState, meta: PillStateMeta = {}): void {
    this.playState = state;
    this.stateLabel = meta.label ?? '';
    this.translatingFlag = meta.translating ?? false;
    this.render();
  }

  private toggle(): void {
    this.active = !this.active;
    this.playState = 'idle';
    this.render();
    try {
      localStorage.setItem(STORAGE_KEY, this.active ? '1' : '0');
    } catch {
      /* ignore */
    }
    this.cb.onToggle(this.active);
  }

  private render(): void {
    const loading = this.active && this.playState === 'loading';
    const playing = this.active && this.playState === 'playing';
    this.el.classList.toggle('active', this.active);
    this.el.classList.toggle('loading', loading);
    this.el.classList.toggle('playing', playing);
    this.toggleBtn.setAttribute('aria-pressed', this.active ? 'true' : 'false');
    this.gearBtn.tabIndex = this.active ? 0 : -1;
    this.gearBtn.setAttribute('aria-hidden', this.active ? 'false' : 'true');

    // Icon stays the LL mark in idle + playing states (the .pill.playing
    // class drives the bar animation in CSS). Loading swaps in the
    // translate glyph (when this is a translation hop) or the three
    // staggered dots (generic synthesis loading).
    const iconHost = this.toggleBtn.querySelector('.pill-icon')!;
    iconHost.innerHTML = loading
      ? (this.translatingFlag ? ICON_TRANSLATE : ICON_DOTS)
      : ICON_LLMARK;
    iconHost.classList.toggle('icon-translate', loading && this.translatingFlag);

    if (!this.active) {
      this.statusEl.textContent = this.tr('brand');
    } else if (loading) {
      this.statusEl.textContent = this.stateLabel || this.tr('pillLoadingSpeak');
    } else if (playing) {
      this.statusEl.textContent = this.stateLabel || this.tr('pillPlaying');
    } else {
      this.statusEl.textContent = this.stateLabel || this.tr('pillIdle');
    }
  }

  setLanguage(tr: TranslateFn): void {
    this.tr = tr;
    this.render();
  }
}
